import type { VoiceId } from './voice';

/**
 * One-shot stingers. They are not loops.
 * Victory and defeat fade the music out. The power stinger plays over it.
 */

export type StingerId = 'victory' | 'defeat' | 'power';

export const STINGER_SECONDS: Record<StingerId, number> = {
  victory: 5.4,
  defeat: 4.4,
  power: 2.2,
};

const STINGER_FILE: Record<StingerId, string> = {
  victory: 'stinger-victory-bright',
  defeat: 'stinger-defeat-bright',
  power: 'stinger-power-bright',
};

export function stingerFile(id: StingerId): string {
  return STINGER_FILE[id];
}

/** Ogg first. M4a is the fallback. */
export function stingerSources(id: StingerId, support: { ogg: boolean; m4a: boolean }): string[] {
  const file = STINGER_FILE[id];
  const ogg = `/audio/${file}.ogg`;
  const m4a = `/audio/${file}.m4a`;
  if (support.ogg && support.m4a) return [ogg, m4a];
  if (support.ogg) return [ogg];
  if (support.m4a) return [m4a];
  return [ogg, m4a];
}

/** Win and loss only. A tie has no stinger. */
export function stingerForResult(winner: 0 | 1 | 'tie'): StingerId | null {
  if (winner === 0) return 'victory';
  if (winner === 1) return 'defeat';
  return null;
}

/** The human signature meter, on the same once-per-fill edge as "Power ready". */
export function stingerForVoice(id: VoiceId | null): StingerId | null {
  return id === 'power-ready' ? 'power' : null;
}

/** Victory and defeat take the music down. The power stinger leaves it alone. */
export function stingerDucksMusic(id: StingerId): boolean {
  return id !== 'power';
}

/**
 * One-shots on the master bus, so mute and the master volume apply.
 * A new stinger replaces one that is still playing. Nothing here loops.
 */
export class Stingers {
  private readonly buffers = new Map<StingerId, AudioBuffer>();
  private readonly loads = new Map<StingerId, Promise<AudioBuffer | null>>();
  private source: AudioBufferSourceNode | null = null;
  private generation = 0;

  constructor(
    private readonly context: () => AudioContext | null,
    private readonly master: () => GainNode | null,
    private readonly muted: () => boolean,
    private readonly duckMusic: (seconds: number) => void,
    private readonly releaseMusic: () => void,
  ) {}

  preload(): void {
    (Object.keys(STINGER_FILE) as StingerId[]).forEach((id) => void this.load(id));
  }

  play(id: StingerId): void {
    if (this.muted()) return;
    const generation = ++this.generation;
    this.stopSource();
    void this.start(id, generation);
  }

  halt(): void {
    this.generation += 1;
    this.stopSource();
    this.releaseMusic();
  }

  private async start(id: StingerId, generation: number): Promise<void> {
    const ctx = this.context();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
    const buffer = await this.load(id);
    if (generation !== this.generation || this.muted() || !buffer) return;
    const master = this.master();
    const live = this.context();
    if (!master || !live) return;
    this.stopSource();
    const src = live.createBufferSource();
    src.buffer = buffer;
    src.loop = false;
    src.connect(master);
    src.onended = () => {
      if (this.source === src) this.source = null;
    };
    try {
      src.start();
    } catch {
      return;
    }
    this.source = src;
    if (stingerDucksMusic(id)) this.duckMusic(buffer.duration);
  }

  private stopSource(): void {
    const src = this.source;
    this.source = null;
    if (!src) return;
    try {
      src.stop();
    } catch {
      // Already finished.
    }
  }

  private load(id: StingerId): Promise<AudioBuffer | null> {
    const cached = this.buffers.get(id);
    if (cached) return Promise.resolve(cached);
    const pending = this.loads.get(id);
    if (pending) return pending;
    const job = this.decode(id).then((buffer) => {
      if (buffer) this.buffers.set(id, buffer);
      if (this.loads.get(id) === job) this.loads.delete(id);
      return buffer;
    });
    this.loads.set(id, job);
    return job;
  }

  private async decode(id: StingerId): Promise<AudioBuffer | null> {
    const ctx = this.context();
    if (!ctx || typeof fetch !== 'function') return null;
    for (const url of stingerSources(id, this.support())) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const data = await response.arrayBuffer();
        return await ctx.decodeAudioData(data.slice(0));
      } catch {
        // Try the other file.
      }
    }
    return null;
  }

  private support(): { ogg: boolean; m4a: boolean } {
    if (typeof document === 'undefined') return { ogg: true, m4a: true };
    const audio = document.createElement('audio');
    const yes = (mime: string) => {
      const value = audio.canPlayType(mime);
      return value === 'probably' || value === 'maybe';
    };
    return {
      ogg: yes('audio/ogg; codecs="vorbis"'),
      m4a: yes('audio/mp4; codecs="mp4a.40.2"'),
    };
  }
}
